import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Receipt, Download } from 'lucide-react';
import ErrorDialog from '../ErrorDialog';
import { formatCurrency, formatMonth } from './utils';
import { exportToCSV } from '../../utils/csvExport';
import { getCurrentMonthString } from '../../utils/dateUtils';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '');
const API = `${BACKEND_URL}/api`;

export function ExpenseReport() {
  const [expenseData, setExpenseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('single');
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthString());
  const [startMonth, setStartMonth] = useState(getCurrentMonthString());
  const [endMonth, setEndMonth] = useState(getCurrentMonthString());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [errorDialog, setErrorDialog] = useState({ open: false, message: '', fieldName: '', fieldId: '' });

  const yearOptions = [];
  const currentYear = new Date().getFullYear();
  for (let i = 0; i < 5; i++) {
    yearOptions.push((currentYear - i).toString());
  }

  const validateDateRange = () => {
    if (filterType === 'range' && startMonth > endMonth) {
      setErrorDialog({
        open: true,
        message: 'From date must be earlier than To date',
        fieldName: 'From Month',
        fieldId: 'expense-start-month'
      });
      return false;
    }
    return true;
  };

  const fetchExpenseReport = async (isRetry = false) => {
    if (!validateDateRange()) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let response;
      const config = { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 };
      
      if (filterType === 'single') {
        response = await axios.get(`${API}/expenses/reports/monthly?month=${selectedMonth}`, config);
      } else if (filterType === 'range') {
        response = await axios.get(`${API}/expenses/reports/range?start_month=${startMonth}&end_month=${endMonth}`, config);
      } else if (filterType === 'year') {
        const yearStart = `${selectedYear}-01`;
        const yearEnd = `${selectedYear}-12`;
        response = await axios.get(`${API}/expenses/reports/range?start_month=${yearStart}&end_month=${yearEnd}`, config);
      }
      
      setExpenseData(response.data);
    } catch (error) {
      console.error('Expense report fetch error:', error);
      if (!isRetry && (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED')) {
        setTimeout(() => fetchExpenseReport(true), 2000);
        return;
      }
      if (error.code === 'ERR_NETWORK') {
        toast.error('Network error - check your connection');
      } else {
        toast.error('Failed to load expense report');
      }
    } finally { 
      setLoading(false); 
    }
  };

  useEffect(() => {
    fetchExpenseReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, selectedMonth, startMonth, endMonth, selectedYear]);

  const getDateRangeLabel = () => {
    if (filterType === 'single') {
      return formatMonth(selectedMonth);
    } else if (filterType === 'range') {
      return `${formatMonth(startMonth)} - ${formatMonth(endMonth)}`;
    } else {
      return `Year ${selectedYear}`;
    }
  };

  const handleExportCSV = () => {
    if (!expenseData) {
      toast.error('No data to export');
      return;
    }
    
    const exportData = [];
    
    // Add category breakdown
    if (expenseData.by_category) {
      Object.entries(expenseData.by_category).forEach(([category, amount]) => {
        exportData.push({ category, amount, type: 'By Category' });
      });
    }
    
    // Add person breakdown
    if (expenseData.by_person) {
      Object.entries(expenseData.by_person).forEach(([person, amount]) => {
        exportData.push({ category: person, amount, type: 'By Person' });
      });
    }
    
    const columns = [
      { key: 'type', label: 'Report Type' },
      { key: 'category', label: 'Category/Person' },
      { key: 'amount', label: 'Amount (₹)' }
    ];
    
    const filename = `expense_report_${getDateRangeLabel().replace(/\s/g, '_')}`;
    exportToCSV(exportData, columns, filename);
    toast.success('Expense report exported to CSV');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Expense Report</h3>
            <p className="text-sm text-slate-500">View expenses by category and person</p>
          </div>
          <Button
            data-testid="export-expense-report-csv"
            variant="outline"
            onClick={handleExportCSV}
            disabled={!expenseData}
            className="rounded-xl h-9 w-9 p-0"
            title="Export CSV"
          >
            <Download className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Filter Type Selection */}
        <div className="flex flex-wrap items-center gap-3">
          <Label className="text-sm font-medium">Filter:</Label>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterType('single')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                filterType === 'single' 
                  ? 'bg-slate-900 text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Single Month
            </button>
            <button
              onClick={() => setFilterType('range')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                filterType === 'range' 
                  ? 'bg-slate-900 text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Date Range
            </button>
            <button
              onClick={() => setFilterType('year')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                filterType === 'year' 
                  ? 'bg-slate-900 text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Full Year
            </button>
          </div>
        </div>

        {/* Date Inputs based on filter type */}
        <div className="flex flex-wrap items-center gap-3">
          {filterType === 'single' && (
            <div className="flex items-center gap-2">
              <Label className="text-sm">Month:</Label>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="h-10 px-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
          )}
          
          {filterType === 'range' && (
            <div className="w-full sm:w-auto">
              <div className="sm:hidden bg-slate-50 rounded-xl p-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">From</Label>
                    <input
                      id="expense-start-month"
                      type="month"
                      value={startMonth}
                      onChange={(e) => setStartMonth(e.target.value)}
                      className="w-full h-9 px-2 text-sm rounded-lg border border-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">To</Label>
                    <input
                      id="expense-end-month"
                      type="month"
                      value={endMonth}
                      onChange={(e) => setEndMonth(e.target.value)}
                      className="w-full h-9 px-2 text-sm rounded-lg border border-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm">From:</Label>
                  <input
                    id="expense-start-month-desktop"
                    type="month"
                    value={startMonth}
                    onChange={(e) => setStartMonth(e.target.value)}
                    className="h-10 px-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm">To:</Label>
                  <input
                    id="expense-end-month-desktop"
                    type="month"
                    value={endMonth}
                    onChange={(e) => setEndMonth(e.target.value)}
                    className="h-10 px-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}
          
          {filterType === 'year' && (
            <div className="flex items-center gap-2">
              <Label className="text-sm">Year:</Label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-32 h-10 rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map(year => (
                    <SelectItem key={year} value={year}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* Error Dialog */}
      <ErrorDialog
        open={errorDialog.open}
        onClose={() => setErrorDialog({ ...errorDialog, open: false })}
        message={errorDialog.message}
        fieldName={errorDialog.fieldName}
        fieldId={errorDialog.fieldId}
      />

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-6 h-6 border-2 border-slate-300 border-t-amber-500 rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Summary */}
          {expenseData && (
            <Card className="p-6 border-0 rounded-2xl shadow-lg bg-gradient-to-br from-slate-800 to-slate-900">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Expenses</p>
                  <p className="text-3xl font-bold text-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    {formatCurrency(expenseData.total_expenses)}
                  </p>
                  <p className="text-slate-400 text-sm mt-1">{getDateRangeLabel()} • {expenseData.total_count} expenses</p>
                </div>
              </div>
            </Card>
          )}

          {/* Monthly Breakdown for range/year */}
          {expenseData?.by_month && Object.keys(expenseData.by_month).length > 0 && (
            <Card className="border-0 rounded-2xl shadow-lg bg-white overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <h4 className="font-semibold text-slate-800">Monthly Breakdown</h4>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Month</th>
                      <th className="px-4 py-3 text-right font-semibold text-slate-700">Amount</th>
                      <th className="px-4 py-3 text-right font-semibold text-slate-700">Count</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {Object.entries(expenseData.by_month)
                      .sort((a, b) => b[0].localeCompare(a[0]))
                      .map(([month, data]) => (
                      <tr key={month} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-900">{formatMonth(month)}</td>
                        <td className="px-4 py-3 text-right text-amber-600">{formatCurrency(data.total)}</td>
                        <td className="px-4 py-3 text-right text-slate-600">{data.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* By Category */}
          {expenseData?.by_category && Object.keys(expenseData.by_category).length > 0 && (
            <Card className="border-0 rounded-2xl shadow-lg bg-white overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <h4 className="font-semibold text-slate-800">By Category</h4>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Category</th>
                      <th className="px-4 py-3 text-right font-semibold text-slate-700">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {Object.entries(expenseData.by_category)
                      .sort((a, b) => b[1] - a[1])
                      .map(([category, amount]) => (
                      <tr key={category} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-900">{category}</td>
                        <td className="px-4 py-3 text-right text-amber-600">{formatCurrency(amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* By Person */}
          {expenseData?.by_person && Object.keys(expenseData.by_person).length > 0 && (
            <Card className="border-0 rounded-2xl shadow-lg bg-white overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <h4 className="font-semibold text-slate-800">By Person</h4>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Submitted By</th>
                      <th className="px-4 py-3 text-right font-semibold text-slate-700">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {Object.entries(expenseData.by_person)
                      .sort((a, b) => b[1] - a[1])
                      .map(([person, amount]) => (
                      <tr key={person} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-900">{person}</td>
                        <td className="px-4 py-3 text-right text-amber-600">{formatCurrency(amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* No Data Message */}
          {(!expenseData?.by_category || Object.keys(expenseData.by_category).length === 0) && 
           (!expenseData?.by_person || Object.keys(expenseData.by_person).length === 0) && (
            <Card className="border-0 rounded-2xl shadow-lg bg-white p-12 text-center">
              <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No expenses found for the selected period</p>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
